<template>
  <div class="sales-report-chart">
    <div class="chart-header">
      <h3>{{ title }}</h3>
      <div class="chart-controls">
        <a-radio-group v-model:value="chartType" size="small" button-style="solid">
          <a-radio-button value="line">折线图</a-radio-button>
          <a-radio-button value="bar">柱状图</a-radio-button>
        </a-radio-group>
        <a-select v-model:value="timeRange" :options="timeRangeOptions" style="width: 120px; margin-left: 8px" size="small" />
      </div>
    </div>
    
    <a-spin :spinning="loading">
      <div class="chart-container" :style="{ height: `${height}px` }">
        <v-chart 
          :option="chartOption" 
          autoresize 
        />
      </div>
    </a-spin>
    
    <div class="chart-footer">
      <a-alert v-if="showAverage" :message="`平均销售额: ${averageSales} 元`" type="info" banner />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart, BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import { Customer } from '@/types';

// 注册必要的组件
use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent
]);

export default defineComponent({
  name: 'SalesReportChart',
  components: {
    VChart
  },
  props: {
    customerData: {
      type: Object as () => Customer,
      required: true
    },
    title: {
      type: String,
      default: '销售数据报表'
    },
    height: {
      type: Number,
      default: 350
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    showAverage: {
      type: Boolean,
      default: true
    }
  },
  emits: ['action'],
  setup(props, { emit }) {
    const loading = ref(false);
    const chartType = ref<'line' | 'bar'>('line');
    const timeRange = ref('month6');
    
    const timeRangeOptions = [
      { value: 'month3', label: '近3个月' },
      { value: 'month6', label: '近6个月' },
      { value: 'year1', label: '近1年' }
    ];
    
    // 模拟销售数据
    const salesData = ref<{date: string; value: number}[]>([]);
    
    // 计算平均销售额
    const averageSales = computed(() => {
      if (salesData.value.length === 0) return '0';
      const total = salesData.value.reduce((sum, item) => sum + item.value, 0);
      return (total / salesData.value.length).toFixed(2);
    });
    
    // 图表配置
    const chartOption = computed(() => {
      return {
        tooltip: {
          trigger: 'axis',
          formatter: function(params: any) {
            const data = params[0];
            return `${data.name}<br/>${data.marker}${data.seriesName}: ${data.value} 元`;
          }
        },
        legend: {
          show: props.showLegend,
          data: ['销售额'],
          right: 10,
          top: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: props.showLegend ? '40px' : '20px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: salesData.value.map(item => item.date),
          axisLine: {
            lineStyle: {
              color: '#ccc'
            }
          },
          axisLabel: {
            rotate: 30,
            margin: 15
          }
        },
        yAxis: {
          type: 'value',
          name: '销售额 (元)',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#ccc'
            }
          },
          splitLine: {
            lineStyle: {
              color: '#eee'
            }
          }
        },
        series: [
          {
            name: '销售额',
            type: chartType.value,
            data: salesData.value.map(item => item.value),
            smooth: chartType.value === 'line',
            symbol: chartType.value === 'line' ? 'circle' : 'none',
            symbolSize: 6,
            showSymbol: false,
            itemStyle: {
              color: '#1890ff'
            },
            emphasis: {
              itemStyle: {
                color: '#40a9ff'
              }
            },
            markLine: {
              data: [
                {
                  type: 'average',
                  name: '平均值',
                  label: {
                    position: 'middle',
                    formatter: '平均: {c}'
                  }
                }
              ]
            }
          }
        ],
        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100
          }
        ]
      };
    });
    
    // 生成模拟数据
    const generateMockData = () => {
      loading.value = true;
      
      // 生成数据的月份数量
      let months = 3;
      if (timeRange.value === 'month6') months = 6;
      if (timeRange.value === 'year1') months = 12;
      
      // 获取当前日期
      const now = new Date();
      const result = [];
      
      // 客户ID作为随机种子
      const seed = props.customerData.id ? parseInt(props.customerData.id.substring(0, 8), 16) % 1000 : 123;
      
      // 生成过去几个月的数据
      for (let i = months - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const dateStr = `${year}-${month.toString().padStart(2, '0')}`;
        
        // 使用伪随机数生成销售额，这样每次刷新页面数据都相同
        const pseudoRandom = Math.sin(seed * (i + 1)) * 10000;
        const baseValue = 10000 + Math.abs(pseudoRandom) * 5000;
        
        // 添加一些趋势性变化
        const trendFactor = 1 + (i / months) * 0.5; // 随时间增长
        const seasonalFactor = 1 + Math.sin(month / 12 * Math.PI * 2) * 0.2; // 季节性波动
        
        const value = Math.round(baseValue * trendFactor * seasonalFactor);
        
        result.push({
          date: dateStr,
          value
        });
      }
      
      // 延迟加载效果
      setTimeout(() => {
        salesData.value = result;
        loading.value = false;
      }, 800);
    };
    
    // 处理组件操作
    const handleAction = (actionType: string) => {
      emit('action', {
        type: actionType,
        component: 'SalesReportChart',
        data: {
          customerData: props.customerData,
          salesData: salesData.value
        }
      });
    };
    
    // 监听时间范围变化
    watch(timeRange, () => {
      generateMockData();
    });
    
    // 加载初始数据
    onMounted(() => {
      generateMockData();
    });
    
    return {
      loading,
      chartType,
      timeRange,
      timeRangeOptions,
      chartOption,
      salesData,
      averageSales,
      handleAction
    };
  }
});
</script>

<style lang="less" scoped>
.sales-report-chart {
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
    }
  }
  
  .chart-container {
    width: 100%;
    min-height: 300px;
  }
  
  .chart-footer {
    margin-top: 16px;
  }
}
</style> 